import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { GraduationCap, Sparkles, Loader2, ChevronLeft, ChevronRight, Save, Check } from "lucide-react";
import { api } from "../lib/api";
import { formatHebrewRef } from "../lib/hebrewRef";
import { formatDateForLang } from "../lib/dateFormat";
import LessonView from "./LessonView";
import { toast } from "sonner";

/**
 * LessonBuilder — source-grounded Torah lesson generator dialog.
 *
 * Flow: 3 short steps, same on mobile & desktop (with enough room to grow).
 *   1. Choose commentators to include
 *   2. Choose level + length + style
 *   3. Review → Generate → LessonView
 *
 * The generated lesson is strictly source-grounded: the backend feeds the
 * LLM only the verses and commentaries we already fetched from Sefaria.
 */

// Stable id lists — labels are resolved at render time from i18n so the
// Hebrew flow stays byte-identical when t() returns the Hebrew string.
const LEVEL_IDS  = ["beginner", "intermediate", "advanced", "yeshiva"];
const LENGTH_IDS = ["3min", "5min", "10min", "20min", "full"];
const STYLE_IDS  = ["simple", "inspirational", "deep", "compare", "qa", "parasha", "children", "shabbat_table"];

// Note templates — `id` is stable, `label` and `text` are looked up via t().
// The Hebrew copy lives in he.json so the prompt sent to the LLM is also
// translated: an English-mode user gets an English-prompt template, which
// in turn produces English drafts for English lessons.
const NOTE_TEMPLATE_IDS = [
  "kids", "musar", "shabbat_table", "chassidic", "bar_mitzvah", "shul", "yeshiva", "story", "modern",
];



const ALL_COMMENTATORS = [
  // Rishonim - פשט ועומק
  { id: "Rashi", he: "רש״י", category: "ראשונים" },
  { id: "Ramban", he: "רמב״ן", category: "ראשונים" },
  { id: "Ibn Ezra", he: "אבן עזרא", category: "ראשונים" },
  { id: "Rashbam", he: "רשב״ם", category: "ראשונים" },
  { id: "Radak", he: "רד״ק", category: "ראשונים" },
  { id: "Chizkuni", he: "חזקוני", category: "ראשונים" },
  { id: "Bekhor Shor", he: "בכור שור", category: "ראשונים" },
  { id: "Ralbag", he: "רלב״ג", category: "ראשונים" },
  { id: "Abarbanel", he: "אברבנאל", category: "ראשונים" },
  
  // Acharonim - פרשנות מאוחרת
  { id: "Or HaChaim", he: "אור החיים", category: "אחרונים" },
  { id: "Sforno", he: "ספורנו", category: "אחרונים" },
  { id: "Kli Yakar", he: "כלי יקר", category: "אחרונים" },
  { id: "Malbim", he: "מלבי״ם", category: "אחרונים" },
  { id: "Metzudat David", he: "מצודת דוד", category: "אחרונים" },
  { id: "Metzudat Zion", he: "מצודת ציון", category: "אחרונים" },
  { id: "Haamek Davar", he: "העמק דבר", category: "אחרונים" },
  { id: "Alshich", he: "אלשיך", category: "אחרונים" },
  { id: "Chatam Sofer", he: "חתם סופר", category: "אחרונים" },
  
  // Targumim - תרגומים
  { id: "Onkelos", he: "אונקלוס", category: "תרגומים" },
  { id: "Targum Jonathan", he: "תרגום יונתן", category: "תרגומים" },
];

// Mishnah-specific commentators — verified live against Sefaria on all
// 63 tractates (every one returns real text for at least one mishnah).
// These are surfaced as a *separate* picker group in step 1 when the
// basket contains any Mishnah items, so the user never has to pick
// Bartenura when they're studying Bereshit, or Rashi when they're
// studying Berakhot.
const MISHNAH_COMMENTATORS_UI = [
  { id: "Bartenura",        he: "ברטנורא",              category: "פרשני המשנה" },
  { id: "Rambam",           he: "פירוש המשנה לרמב״ם",   category: "פרשני המשנה" },
  { id: "Tosafot Yom Tov",  he: "תוספות יום טוב",        category: "פרשני המשנה" },
  { id: "Yachin",           he: "תפארת ישראל יכין",     category: "פרשני המשנה" },
  { id: "Melekhet Shelomoh", he: "מלאכת שלמה",          category: "פרשני המשנה" },
];

// Talmud-specific commentators. Same shape as the Tanakh / Mishnah
// lists; the Sefaria refs they use ("Rashi on Berakhot", "Tosafot on
// Berakhot") are distinct from the Tanakh-side Rashi/Tosafot — so no
// cross-corpus contamination is ever possible.
const TALMUD_COMMENTATORS_UI = [
  { id: "Rashi",          he: "רש״י",          category: "פרשני הגמרא" },
  { id: "Tosafot",        he: "תוספות",        category: "פרשני הגמרא" },
  { id: "Meiri",          he: "מאירי",         category: "פרשני הגמרא" },
  { id: "Tosafot HaRosh", he: "תוספות הרא״ש",  category: "פרשני הגמרא" },
  { id: "Penei Yehoshua", he: "פני יהושע",     category: "פרשני הגמרא" },
];

export default function LessonBuilder({
  open,
  onClose,
  book,           // slug, e.g. "Genesis" (single-verse mode)
  bookHe,
  verses,         // [{chapter, verse}] — single-book mode
  mode = "single", // "single" | "basket"
  basketItems = [], // used when mode === "basket"
}) {
  const { t, i18n } = useTranslation();
  const { isEn } = useLanguage();
  const [step, setStep] = useState(1);
  // Render-time picker lists. Hebrew values come from he.json (byte-
  // identical to the legacy hard-coded labels). English values come
  // from en.json. Single source of truth, no parallel arrays to drift.
  const LEVELS  = useMemo(() => LEVEL_IDS.map((id)  => ({ id, label: t(`lesson_builder.level_${id}`) })),  [t]);
  const LENGTHS = useMemo(() => LENGTH_IDS.map((id) => ({ id, label: t(`lesson_builder.length_${id}`) })), [t]);
  const STYLES  = useMemo(() => STYLE_IDS.map((id)  => ({ id, label: t(`lesson_builder.style_${id}`) })),  [t]);
  const NOTE_TEMPLATES = useMemo(
    () => NOTE_TEMPLATE_IDS.map((id) => ({
      id,
      label: t(`lesson_builder.tpl_${id}_label`),
      text:  t(`lesson_builder.tpl_${id}_text`),
    })),
    [t]
  );
  const [selectedCommentators, setSelectedCommentators] = useState([
    "Rashi",
    "Ramban",
    "Ibn Ezra",
  ]);
  // Mishnah commentators are kept in a separate state so picking one
  // doesn't unselect a Tanakh commentator (the two corpora are routed
  // independently in the backend). Default = top 2 verified picks.
  const [selectedMishnahCommentators, setSelectedMishnahCommentators] =
    useState(["Bartenura", "Tosafot Yom Tov"]);
  // Talmud commentator picks — Rashi + Tosafot is the universal default
  // pairing for any daf, matching the way the Daf Yomi is typically
  // learned.
  const [selectedTalmudCommentators, setSelectedTalmudCommentators] =
    useState(["Rashi", "Tosafot"]);
  const [level, setLevel] = useState("intermediate");
  const [length, setLength] = useState("5min");
  const [style, setStyle] = useState("simple");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ phase: "queued", label: "", pct: 0, totalVerses: 0, sampledVerses: 0 });
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const versesLabel = useMemo(() => {
    if (mode === "basket") {
      const vCount = basketItems.filter((i) => i.type === "verse").length;
      const cCount = basketItems.filter((i) => i.type === "commentary").length;
      const nCount = basketItems.filter((i) => i.type === "note").length;
      const parts = [];
      if (vCount) parts.push(t("lesson_builder.verses_count", { count: vCount }));
      if (cCount) parts.push(t("lesson_builder.commentaries_count", { count: cCount }));
      if (nCount) parts.push(t("lesson_builder.notes_count", { count: nCount }));
      return parts.join(" · ");
    }
    if (!verses || verses.length === 0) return "";
    if (verses.length === 1) {
      const v = verses[0];
      return formatHebrewRef(bookHe || book, v.chapter, v.verse);
    }
    const first = verses[0];
    const last = verses[verses.length - 1];
    return `${formatHebrewRef(bookHe || book, first.chapter, first.verse)}–${formatHebrewRef(bookHe || book, last.chapter, last.verse)}`;
  }, [mode, basketItems, verses, book, bookHe, t]);

  // What corpora does this build draw from? Drives which commentator
  // pickers appear in step 1. `mode === "single"` is always Tanakh.
  const corpusMix = useMemo(() => {
    if (mode !== "basket") return { hasTanakh: true, hasMishnah: false, hasTalmud: false };
    let t = false, m = false, g = false;
    for (const it of basketItems) {
      if (it.type === "note") continue;
      if (it.source === "mishnah") m = true;
      else if (it.source === "talmud") g = true;
      else t = true;
    }
    // Empty basket falls back to Tanakh so the picker still renders.
    if (!t && !m && !g) t = true;
    return { hasTanakh: t, hasMishnah: m, hasTalmud: g };
  }, [mode, basketItems]);

  const toggleMishnahCommentator = (id) => {
    setSelectedMishnahCommentators((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleTalmudCommentator = (id) => {
    setSelectedTalmudCommentators((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleCommentator = (id) => {
    setSelectedCommentators((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const reset = () => {
    setStep(1);
    setResult(null);
    setNotes("");
    setSaved(false);
    setProgress({ phase: "queued", label: "", pct: 0, totalVerses: 0, sampledVerses: 0 });
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const generate = async () => {
    setLoading(true);
    setProgress({ phase: "queued", label: t("lesson_builder.starting"), pct: 5, totalVerses: 0, sampledVerses: 0 });
    try {
      let payload;
      if (mode === "basket") {
        if (!basketItems || basketItems.length === 0) {
          toast.error(t("lesson_builder.basket_empty"));
          setLoading(false);
          return;
        }
        // New multi-source API shape — server accepts heterogeneous `sources`.
        // Each source carries its `source` tag ("tanakh" | "mishnah") so the
        // backend can apply the right per-corpus commentator filter and never
        // ask Sefaria for Rashi-on-Berakhot or Bartenura-on-Bereshit.
        payload = {
          sources: basketItems.map((it) => {
            if (it.type === "verse") {
              return {
                type: "verse",
                book: it.book,
                chapter: it.chapter,
                verse: it.verse,
                source: it.source,
              };
            }
            if (it.type === "commentary") {
              return {
                type: "commentary",
                book: it.book,
                chapter: it.chapter,
                verse: it.verse,
                commentator: it.commentator,
                source: it.source,
              };
            }
            if (it.type === "chapter") {
              return {
                type: "chapter",
                book: it.book,
                chapter: it.chapter,
                source: it.source,
              };
            }
            return { type: "note", text: it.text };
          }),
          // Legacy field: the global Tanakh commentator list. Kept as-is
          // for back-compat with existing job-progress polling code.
          commentators: selectedCommentators,
          // New field: a per-corpus map so the backend picks the right
          // catalog per source. Tanakh sources get Rashi/Ramban/etc.,
          // Mishnah sources get Bartenura/Tosafot Yom Tov/etc.
          commentators_by_source: {
            tanakh: selectedCommentators,
            mishnah: selectedMishnahCommentators,
            talmud: selectedTalmudCommentators,
          },
          level,
          length,
          style,
          user_notes: notes.trim() || undefined,
        };
      } else {
        if (!verses || verses.length === 0) {
          toast.error(t("lesson_builder.no_verses_selected"));
          setLoading(false);
          return;
        }
        payload = {
          book,
          verses,
          commentators: selectedCommentators,
          level,
          length,
          style,
          user_notes: notes.trim() || undefined,
        };
      }
      // Inject the active UI language so the backend renders the lesson in
      // English when the user is in English mode. Hebrew stays the default.
      if (payload) {
        payload.lang = i18n.language === "en" ? "en" : "he";
      }
      // Async job pattern — POST returns {job_id, status:"pending"} fast,
      // we then poll /lessons/jobs/{id} until status is "done" or "error".
      const submitted = await api.buildLesson(payload);
      const jobId = submitted?.job_id;
      if (!jobId) {
        // Legacy synchronous response (fallback)
        setResult(submitted);
        setSaved(false);
        setStep(3);
        return;
      }
      setProgress({
        phase: "queued",
        label: t("lesson_builder.queued"),
        pct: 5,
        totalVerses: submitted.total_verses || 0,
        sampledVerses: submitted.sampled_verses || 0,
      });
      // Poll at 1.2 s so the live "✓ קיבלתי את רש"י" / "📜 שולף..."
      // label updates surface to the user nearly in real time during the
      // 5–35 % fetch phase. The 6-minute timeout is for very large
      // (20-min) lessons that can take 2–3 minutes inside the LLM.
      const POLL_INTERVAL_MS = 1200;
      const TIMEOUT_MS = 6 * 60 * 1000;
      const t0 = Date.now();
      // Slowly creep the bar between phase updates so it never feels frozen.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        if (Date.now() - t0 > TIMEOUT_MS) {
          throw new Error(t("lesson_builder.too_slow"));
        }
        let job;
        try {
          job = await api.lessonJobStatus(jobId);
        } catch (e) {
          // Transient network blip — keep polling unless 404
          if (e?.response?.status === 404) throw e;
          continue;
        }
        const phase = job?.phase || "queued";
        const label = job?.phase_label || "";
        const backendPct = typeof job?.progress === "number" ? job.progress : 0;
        // Trust the backend's progress completely now that we update it
        // per-verse-fetch and tick a creep on the server during composing.
        // We only guard against the bar going backwards.
        setProgress((p) => {
          const pct = Math.max(p.pct, backendPct);
          return { ...p, phase, label, pct };
        });
        if (job?.status === "done" && job?.result) {
          setProgress((p) => ({ ...p, phase: "done", label: t("lesson_builder.lesson_ready"), pct: 100 }));
          // Tiny delay so the user sees the bar fill to 100 before the lesson appears.
          await new Promise((r) => setTimeout(r, 350));
          setResult(job.result);
          setSaved(false);
          setStep(3);
          break;
        }
        if (job?.status === "error") {
          throw new Error(job?.error || t("lesson_builder.build_failed_generic"));
        }
      }
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || e?.message || t("lesson_builder.save_failed_generic");
      toast.error(t("lesson_builder.build_failed_with_msg", { msg }));
    } finally {
      setLoading(false);
    }
  };

  const saveLesson = async () => {
    if (!result?.lesson) return;
    setSaving(true);
    try {
      const title =
        result?.lesson?.title?.trim() ||
        t("lesson_builder.default_lesson_title", {
          date: formatDateForLang(new Date(), isEn ? "en" : "he", { dateStyle: "short" }),
        });
      await api.saveLesson({
        title,
        lesson: result.lesson,
        meta: result.meta || null,
      });
      setSaved(true);
      toast.success(t("lesson_builder.saved_to_my_lessons"));
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || t("lesson_builder.save_failed_generic");
      toast.error(t("lesson_builder.save_failed_with_msg", { msg }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent
        dir="rtl"
        className="max-w-3xl md:max-w-4xl max-h-[92vh] overflow-y-auto bg-background border border-gold/30"
        data-testid="lesson-builder"
      >
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-2xl text-foreground flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-gold" strokeWidth={1.5} />
            {t("lesson_builder.title_torah")}
          </DialogTitle>
          {versesLabel && (
            <p className="font-latin italic text-xs text-muted-foreground tracking-widest">
              {versesLabel}
            </p>
          )}
        </DialogHeader>

        {/* Step 1 — commentators */}
        {step === 1 && (
          <div className="space-y-5 pt-3" data-testid="lesson-step-1">
            {corpusMix.hasTanakh && (
            <div data-testid="lesson-commentator-section-tanakh">
              <h4 className="font-heading text-sm text-foreground mb-2 flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className="inline-block h-2.5 w-2.5 rounded-sm"
                  style={{ background: "#1a2b4a" }}
                />
                {t("lesson_builder.select_tanakh_commentators")}
              </h4>
              <p className="text-xs text-muted-foreground mb-3">
                {t("lesson_builder.select_tanakh_note")}
              </p>
              <div
                className="flex flex-wrap gap-2"
                data-testid="lesson-commentator-picker"
              >
                {ALL_COMMENTATORS.map((c) => {
                  const sel = selectedCommentators.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleCommentator(c.id)}
                      data-testid={`lesson-commentator-toggle-${c.id.replace(/ /g, "-")}`}
                      data-selected={sel ? "true" : "false"}
                      className={`px-3 py-1.5 rounded-sm border font-heading text-sm transition-all
                        ${sel
                          ? "border-gold bg-gold/20 text-gold"
                          : "border-border/60 text-foreground hover:border-gold/60 hover:bg-gold/5"}`}
                    >
                      {isEn ? c.id : c.he}
                    </button>
                  );
                })}
              </div>
            </div>
            )}

            {corpusMix.hasMishnah && (
            <div data-testid="lesson-commentator-section-mishnah">
              <h4 className="font-heading text-sm text-foreground mb-2 flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className="inline-block h-2.5 w-2.5 rounded-sm"
                  style={{ background: "#23807a" }}
                />
                {t("lesson_builder.select_mishnah_commentators")}
              </h4>
              <p className="text-xs text-muted-foreground mb-3">
                {t("lesson_builder.select_mishnah_note")}
              </p>
              <div
                className="flex flex-wrap gap-2"
                data-testid="lesson-mishnah-commentator-picker"
              >
                {MISHNAH_COMMENTATORS_UI.map((c) => {
                  const sel = selectedMishnahCommentators.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleMishnahCommentator(c.id)}
                      data-testid={`lesson-mishnah-commentator-toggle-${c.id.replace(/ /g, "-")}`}
                      data-selected={sel ? "true" : "false"}
                      className={`px-3 py-1.5 rounded-sm border font-heading text-sm transition-all
                        ${sel
                          ? "border-gold bg-gold/20 text-gold"
                          : "border-border/60 text-foreground hover:border-gold/60 hover:bg-gold/5"}`}
                    >
                      {isEn ? c.id : c.he}
                    </button>
                  );
                })}
              </div>
            </div>
            )}

            {corpusMix.hasTalmud && (
            <div data-testid="lesson-commentator-section-talmud">
              <h4 className="font-heading text-sm text-foreground mb-2 flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className="inline-block h-2.5 w-2.5 rounded-sm"
                  style={{ background: "#4a2c19" }}
                />
                {t("lesson_builder.select_talmud_commentators")}
              </h4>
              <p className="text-xs text-muted-foreground mb-3">
                {t("lesson_builder.select_talmud_note")}
              </p>
              <div
                className="flex flex-wrap gap-2"
                data-testid="lesson-talmud-commentator-picker"
              >
                {TALMUD_COMMENTATORS_UI.map((c) => {
                  const sel = selectedTalmudCommentators.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleTalmudCommentator(c.id)}
                      data-testid={`lesson-talmud-commentator-toggle-${c.id.replace(/ /g, "-")}`}
                      data-selected={sel ? "true" : "false"}
                      className={`px-3 py-1.5 rounded-sm border font-heading text-sm transition-all
                        ${sel
                          ? "border-gold bg-gold/20 text-gold"
                          : "border-border/60 text-foreground hover:border-gold/60 hover:bg-gold/5"}`}
                    >
                      {isEn ? c.id : c.he}
                    </button>
                  );
                })}
              </div>
            </div>
            )}

            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-muted-foreground">
                {[
                  corpusMix.hasTanakh && t("lesson_builder.label_tanakh_count", { count: selectedCommentators.length }),
                  corpusMix.hasMishnah && t("lesson_builder.label_mishnah_count", { count: selectedMishnahCommentators.length }),
                  corpusMix.hasTalmud && t("lesson_builder.label_talmud_count", { count: selectedTalmudCommentators.length }),
                ].filter(Boolean).join(" · ")}
              </span>
              <Button
                onClick={() => setStep(2)}
                disabled={
                  (corpusMix.hasTanakh && selectedCommentators.length === 0) ||
                  (corpusMix.hasMishnah && selectedMishnahCommentators.length === 0) ||
                  (corpusMix.hasTalmud && selectedTalmudCommentators.length === 0)
                }
                data-testid="lesson-next-step-1"
                className="sacred-btn"
              >
                {t("lesson_builder.step_continue")}
                <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2 — level/length/style */}
        {step === 2 && !loading && (
          <div className="space-y-6 pt-3" data-testid="lesson-step-2">
            <Picker
              label={t("lesson_builder.field_level")}
              testid="lesson-level-picker"
              options={LEVELS}
              value={level}
              onChange={setLevel}
            />
            <Picker
              label={t("lesson_builder.field_length")}
              testid="lesson-length-picker"
              options={LENGTHS}
              value={length}
              onChange={setLength}
            />
            <Picker
              label={t("lesson_builder.field_style")}
              testid="lesson-style-picker"
              options={STYLES}
              value={style}
              onChange={setStyle}
            />

            <div>
              <h4 className="font-heading text-sm text-foreground mb-2">
                {t("lesson_builder.personal_note_optional")}
              </h4>
              <p className="text-[11px] text-muted-foreground mb-2 leading-relaxed">
                {t("lesson_builder.personal_note_hint_1")}
                {" "}
                {t("lesson_builder.personal_note_hint_2")}
              </p>
              <div
                className="flex flex-wrap gap-1.5 mb-2.5"
                data-testid="lesson-note-templates"
              >
                {NOTE_TEMPLATES.map((tpl) => (
                  <button
                    key={tpl.label}
                    type="button"
                    onClick={() => setNotes((prev) => (prev ? prev + "\n" + tpl.text : tpl.text))}
                    data-testid={`lesson-note-template-${tpl.id}`}
                    className="px-2.5 py-1 text-[11px] rounded-full border border-gold/30 bg-gold/5 hover:bg-gold/15 hover:border-gold/60 text-foreground/80 hover:text-foreground transition-colors font-heading"
                    title={tpl.text}
                  >
                    {tpl.label}
                  </button>
                ))}
                {notes && (
                  <button
                    type="button"
                    onClick={() => setNotes("")}
                    data-testid="lesson-note-clear"
                    className="px-2.5 py-1 text-[11px] rounded-full border border-destructive/30 bg-destructive/5 hover:bg-destructive/15 text-destructive transition-colors font-heading"
                  >
                    {t("lesson_builder.clear_note")}
                  </button>
                )}
              </div>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t("lesson_builder.note_placeholder")}
                dir="rtl"
                data-testid="lesson-notes-textarea"
                className="min-h-[110px] bg-card border-border/60 focus-visible:ring-gold font-body text-right"
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              <Button
                variant="ghost"
                onClick={() => setStep(1)}
                data-testid="lesson-back-step-2"
                className="sacred-btn-ghost"
              >
                <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                {t("lesson_builder.step_back")}
              </Button>
              <Button
                onClick={generate}
                disabled={loading}
                data-testid="lesson-generate-btn"
                className="sacred-btn-gold"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                    {t("lesson_builder.step_building")}
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                    {t("lesson_builder.step_build")}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Building — full-panel progress with phase narration */}
        {loading && step === 2 && (
          <BuildingProgress progress={progress} />
        )}

        {/* Step 3 — result */}
        {step === 3 && result && (
          <div className="pt-3" data-testid="lesson-step-3">
            <LessonView
              lesson={result.lesson}
              // Enrich meta with the originating ref so the in-lesson
              // "Save to Notebook" button can anchor the entry to the
              // verse(s) the user was studying.
              meta={{
                ...(result.meta || {}),
                book,
                chapter: verses?.[0]?.chapter,
                verse: verses?.[0]?.verse,
                ref_he: versesLabel,
              }}
              onClose={handleClose}
            />
            <div className="flex items-center justify-between mt-4 gap-2 flex-wrap">
              <Button
                variant="ghost"
                onClick={() => setStep(2)}
                data-testid="lesson-regenerate-btn"
                className="sacred-btn-ghost"
              >
                <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                {t("lesson_builder.step_change_settings")}
              </Button>
              <div className="flex items-center gap-2">
                <Button
                  onClick={saveLesson}
                  disabled={saving || saved}
                  data-testid="lesson-save-btn"
                  data-saved={saved ? "true" : "false"}
                  className="sacred-btn-gold"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                      {t("lesson_builder.step_saving")}
                    </>
                  ) : saved ? (
                    <>
                      <Check className="h-4 w-4" strokeWidth={1.5} />
                      {t("lesson_builder.step_saved")}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" strokeWidth={1.5} />
                      {t("lesson_builder.step_save")}
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleClose}
                  data-testid="lesson-done-btn"
                  variant="outline"
                  className="sacred-btn"
                >
                  {t("lesson_builder.step_done")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Picker({ label, testid, options, value, onChange }) {
  return (
    <div data-testid={testid}>
      <h4 className="font-heading text-sm text-foreground mb-2">{label}</h4>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const sel = value === o.id;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onChange(o.id)}
              data-testid={`${testid}-option-${o.id}`}
              data-selected={sel ? "true" : "false"}
              className={`px-3 py-1.5 rounded-sm border font-heading text-sm transition-all
                ${sel
                  ? "border-gold bg-gold/20 text-gold"
                  : "border-border/60 text-foreground hover:border-gold/60 hover:bg-gold/5"}`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Building stage narration. Shows a 5-step pipeline that lights up as the
 * backend phase advances, plus a smooth filling progress bar that creeps
 * forward between updates so it never feels frozen during the 60-120 s
 * Claude call.
 */
/**
 * Visible 4-stage pipeline (product requirement).
 *
 * The backend still emits 6 internal phases — we map them down to these
 * 4 user-facing stages so the experience feels calm and predictable.
 *
 *   stage 0  ←  queued                           0% – 20%
 *   stage 1  ←  fetching_sources, sources_ready  20% – 50%
 *   stage 2  ←  composing, structuring           50% – 90%
 *   stage 3  ←  done                             100%
 */
const BUILD_STAGE_IDS = ["receiving", "fetching", "composing", "done"];
const BUILD_STAGE_ICONS = { receiving: "📋", fetching: "📜", composing: "✦", done: "🎓" };

// Map an internal backend phase string to the visible stage index.
function phaseToStageIdx(phase) {
  switch (phase) {
    case "queued":
      return 0;
    case "fetching_sources":
    case "sources_ready":
      return 1;
    case "composing":
    case "structuring":
      return 2;
    case "done":
      return 3;
    case "error":
      return 0;
    default:
      return 0;
  }
}

/**
 * Short Torah quotes rotated every 8 s during the LLM call to turn dead
 * waiting time into a moment of inspiration. All sourced from the
 * standard rabbinic literature so we never invent or misattribute.
 */
const WAITING_QUOTES = [
  { he: "איזהו חכם — הלומד מכל אדם", src: "פרקי אבות ד׳:א׳" },
  { he: "אם אין אני לי — מי לי, וכשאני לעצמי מה אני", src: "פרקי אבות א׳:י״ד" },
  { he: "יפה שעה אחת של תשובה ומעשים טובים בעולם הזה מכל חיי העולם הבא", src: "פרקי אבות ד׳:י״ז" },
  { he: "כל המקיים נפש אחת — מעלה עליו הכתוב כאילו קיים עולם מלא", src: "סנהדרין ל״ז ע״א" },
  { he: "הפוך בה והפוך בה — דכולא בה", src: "פרקי אבות ה׳:כ״ב" },
  { he: "תפילה בלא כוונה — כגוף בלא נשמה", src: "ספר חרדים פרק נ״ט" },
  { he: "אל תהי בז לכל אדם — שאין לך אדם שאין לו שעה", src: "פרקי אבות ד׳:ג׳" },
  { he: "אל תאמר לכשאפנה אשנה — שמא לא תפנה", src: "פרקי אבות ב׳:ד׳" },
  { he: "תלמוד תורה כנגד כולם", src: "פאה א׳:א׳" },
  { he: "כל המוסיף — גורע", src: "סנהדרין כ״ט ע״א" },
  { he: "הקנאה והתאוה והכבוד — מוציאין את האדם מן העולם", src: "פרקי אבות ד׳:כ״א" },
  { he: "ועשה לך רב, וקנה לך חבר", src: "פרקי אבות א׳:ו׳" },
];

function BuildingProgress({ progress }) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const { phase, label, pct, totalVerses, sampledVerses } = progress;
  const idx = phaseToStageIdx(phase);

  // ---- Time-based progress fallback ---------------------------------
  // The backend reports real progress, but during the first ~5–10 s
  // (queued + initial Sefaria round-trip) it can sit on 5 % long enough
  // that the bar *feels* frozen even though it's actually moving in DB.
  // We compute a smooth time-based estimate that targets ~90 % at 60 s
  // and merge it with the backend value: `displayedPct = max(backend, time)`.
  // Once the job is `done`, we hard-pin to 100.
  const [tick, setTick] = useState(0);
  const [t0] = useState(() => Date.now());
  useEffect(() => {
    if (phase === "done" || phase === "error") return undefined;
    const i = setInterval(() => setTick((x) => x + 1), 500);
    return () => clearInterval(i);
    // tick is intentionally omitted — it's just a re-render trigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const timeBasedPct = (() => {
    if (phase === "done") return 100;
    const elapsed = (Date.now() - t0) / 1000; // seconds
    // Linear ramp: 0 s → 0 %, 60 s → 90 %. Capped at 90 % so we never
    // overtake the backend's actual completion signal.
    // tick is referenced so React sees the dep and re-renders.
    void tick;
    return Math.min(90, Math.round(elapsed * 1.5));
  })();

  const displayedPct = phase === "done"
    ? 100
    : Math.max(pct || 0, timeBasedPct);

  // ---- Inspirational quote rotation (8 s) ---------------------------
  // In Hebrew mode: short Pirkei-Avot citations (`WAITING_QUOTES` —
  // the original constant). In English mode: neutral inspirational
  // learning lines that DO NOT claim to be translations of any
  // source — explicitly avoiding the risk of fabricating an English
  // version of a citation. Both arrays live in i18n locales so the
  // copy can be edited without a code change.
  const quoteCount = WAITING_QUOTES.length;
  const [quoteIdx, setQuoteIdx] = useState(() =>
    Math.floor(Math.random() * quoteCount)
  );
  useEffect(() => {
    if (phase === "done" || phase === "error") return undefined;
    const tm = setInterval(() => {
      setQuoteIdx((i) => (i + 1) % quoteCount);
    }, 8000);
    return () => clearInterval(tm);
  }, [phase, quoteCount]);
  const showQuote = phase !== "done" && phase !== "error";
  const quote = isEn
    ? t(`lesson_builder_wait.quote_${quoteIdx}`)
    : WAITING_QUOTES[quoteIdx];

  return (
    <div
      className="py-12 px-4 sm:px-8 space-y-8"
      data-testid="lesson-building-progress"
    >
      {/* Dancing rabbi pixel-art sprite (12-frame chassidic dance loop). */}
      <div className="flex items-center justify-center">
        <div className="relative">
          <div className="absolute inset-0 bg-gold/25 rounded-full blur-2xl animate-pulse" />
          <div className="relative h-32 w-32 sm:h-36 sm:w-36 rounded-full bg-[#f6efde] border border-gold/30 flex items-center justify-center overflow-hidden shadow-[inset_0_0_18px_rgba(0,0,0,0.05)]">
            <DancingRabbi />
          </div>
        </div>
      </div>

      {/* Title + current label */}
      <div className="text-center space-y-2">
        <h3 className="font-heading text-2xl text-foreground">
          {t("lesson_builder.building_your_lesson")}
        </h3>
        <p
          className="text-sm text-muted-foreground min-h-[1.5em]"
          data-testid="lesson-building-phase-label"
        >
          {label || t("lesson_builder.queued_short")}
        </p>
        {totalVerses > 0 && (
          <p className="text-[11px] text-muted-foreground/70 font-latin tracking-wide">
            {sampledVerses < totalVerses
              ? t("lesson_builder.sampled_n_of", { sampled: sampledVerses, total: totalVerses })
              : t("lesson_builder.total_verses_n", { total: totalVerses })}
          </p>
        )}
      </div>

      {/* Inspirational Torah quote — now lives ABOVE the progress bar and
          stage list so it's front and centre, not buried at the bottom. */}
      {showQuote && quote && (
        <figure
          data-testid="lesson-building-quote"
          className="max-w-md mx-auto transition-opacity duration-700 animate-fade-in-up"
          key={quoteIdx}
        >
          <blockquote className="font-verse text-base text-foreground/85 text-center leading-relaxed">
            ״{quote.he}״
          </blockquote>
          <figcaption className="mt-1.5 text-center text-[11px] text-gold/80 font-latin tracking-wide">
            — {quote.src}
          </figcaption>
        </figure>
      )}

      {/* Progress bar */}
      <div className="max-w-md mx-auto">
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-gold/70 to-gold transition-all duration-700 ease-out"
            style={{ width: `${Math.max(2, Math.min(100, displayedPct))}%` }}
            data-testid="lesson-building-progress-bar"
            data-pct={Math.round(displayedPct)}
          />
        </div>
        <p className="text-center text-[11px] mt-2 text-muted-foreground font-latin">
          {Math.round(displayedPct)}%
        </p>
      </div>

      {/* Stage list — 4 visible stages mapped from 6 internal phases */}
      <ol className="max-w-md mx-auto space-y-2.5">
        {BUILD_STAGE_IDS.map((sid, i) => {
          const isDone = i < idx || phase === "done";
          const isActive = i === idx && phase !== "done";
          return (
            <li
              key={sid}
              data-testid={`lesson-stage-${sid}`}
              className={`flex items-center gap-3 px-3 py-2 rounded-sm border transition-all ${
                isDone
                  ? "border-gold/40 bg-gold/5 text-foreground"
                  : isActive
                    ? "border-gold bg-gold/10 text-foreground shadow-sm"
                    : "border-border/40 bg-transparent text-muted-foreground/70"
              }`}
            >
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                  isDone
                    ? "bg-gold text-background"
                    : isActive
                      ? "bg-gold/20 text-gold"
                      : "bg-muted text-muted-foreground"
                }`}
              >
                {isDone ? "✓" : isActive ? <Loader2 className="h-3 w-3 animate-spin" /> : i + 1}
              </span>
              <span className="text-sm font-heading flex-1">
                {t(`lesson_builder.phase_${sid}`)}
              </span>
              <span className={isDone || isActive ? "text-gold opacity-80" : "opacity-40"}>
                {BUILD_STAGE_ICONS[sid]}
              </span>
            </li>
          );
        })}
      </ol>

      <p className="text-center text-[11px] text-muted-foreground/70 max-w-sm mx-auto leading-relaxed">
        {t("lesson_builder.background_disclaimer_pre")}<br />
        {t("lesson_builder.background_disclaimer_post")}
      </p>
    </div>
  );
}



/**
 * Dancing rabbi loading animation.
 *
 * Plays a short looping MP4 of a hand-drawn chassidic rabbi dancing with
 * a Torah scroll. The video is referenced via process.env.PUBLIC_URL so
 * webpack doesn't try to bundle it. Attributes:
 *   • autoPlay + loop + muted + playsInline  — needed for iOS Safari to
 *     play inline without user-gesture and without sound
 *   • preload="auto" — start loading as soon as the dialog mounts
 *   • disablePictureInPicture, controlsList, etc. — keep it a passive
 *     decorative loop, never a "video the user can interact with"
 * object-fit: cover with a slight zoom-in centers the rabbi in the
 * circular halo so he fills the frame without any letterboxing.
 */
function DancingRabbi() {
  const base = process.env.PUBLIC_URL || "";
  return (
    <video
      data-testid="dancing-rabbi"
      aria-hidden="true"
      className="rabbi-video"
      src={`${base}/brand/dancing-rabbi.mp4?v=1`}
      poster={`${base}/brand/dancing-rabbi-poster.png`}
      autoPlay
      loop
      muted
      playsInline
      preload="auto"
      controls={false}
      disablePictureInPicture
      tabIndex={-1}
    />
  );
}
